import { useQueryClient } from "@tanstack/react-query"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { api, clearSession, refreshSession, setAccessToken, subscribeToSession } from "../api/client"
import type { AuthSession, User } from "../api/types"
import { cleanupPushSubscription } from "../pwa/push"
import { clearPrivateQueryCache } from "../lib/sessionCache"

interface LoginInput {
  login: string
  password: string
  secondFactor?: string
  deviceName?: string
}

interface RegisterInput extends LoginInput {
  displayName: string
  familyName: string
  timezone: string
  invitationToken?: string
}

interface AuthContextValue {
  user: User | null
  accessToken: string | null
  booting: boolean
  login(input: LoginInput): Promise<AuthSession>
  register(input: RegisterInput): Promise<AuthSession>
  logout(): Promise<void>
  updateUser(user: User): void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<AuthSession | null>(null)
  const [booting, setBooting] = useState(true)
  const sessionUserId = useRef<string | null>(null)

  const applySession = useCallback(
    (next: AuthSession | null) => {
      const nextUserId = next?.user.id ?? null
      if (sessionUserId.current !== nextUserId) clearPrivateQueryCache(queryClient)
      sessionUserId.current = nextUserId
      setSession(next)
      if (!next) setAccessToken(null)
    },
    [queryClient]
  )

  useEffect(() => {
    const unsubscribe = subscribeToSession(applySession)

    refreshSession()
      .then(applySession)
      .catch(() => {
        clearSession()
        applySession(null)
      })
      .finally(() => setBooting(false))

    return unsubscribe
  }, [applySession])

  const login = useCallback(
    async (input: LoginInput) => {
      const next = await api.post<AuthSession>("/auth/login", input)
      setAccessToken(next.accessToken)
      applySession(next)
      return next
    },
    [applySession]
  )

  const register = useCallback(
    async (input: RegisterInput) => {
      const next = await api.post<AuthSession>("/auth/register", input)
      setAccessToken(next.accessToken)
      applySession(next)
      return next
    },
    [applySession]
  )

  const logout = useCallback(async () => {
    try {
      await cleanupPushSubscription().catch(() => undefined)
      await api.post<void>("/auth/logout")
    } finally {
      clearSession()
      applySession(null)
    }
  }, [applySession])

  const updateUser = useCallback((user: User) => {
    setSession((current) => (current ? { ...current, user } : current))
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      accessToken: session?.accessToken ?? null,
      booting,
      login,
      register,
      logout,
      updateUser
    }),
    [booting, login, logout, register, session, updateUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error("useAuth must be used inside AuthProvider")
  return value
}
